import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';
import { PaginatedResponse } from '../types';
import {
    Campaign,
    CampaignFieldCatalog,
    CampaignListParams,
    CampaignStatus,
    CreateCampaignRequest,
    PreviewCampaignMappingRequest,
    PreviewCampaignMappingResponse,
    RotateCampaignTokenResponse,
    SaveCampaignMappingRequest,
    SaveCampaignMappingResponse,
    SuggestCampaignMappingResponse,
    UpdateCampaignRequest,
    UpdateSourceFieldsRequest,
} from './types';

const CAMPAIGNS_KEY = 'campaigns';

// Campaign CRUD

export const useCampaigns = (params?: CampaignListParams) => {
    return useQuery<PaginatedResponse<Campaign>>({
        queryKey: [CAMPAIGNS_KEY, params],
        queryFn: async () => {
            const { data } = await httpClient.get<PaginatedResponse<Campaign>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.LIST,
                { params }
            );
            return data;
        },
    });
};

export const useCampaign = (id: string) => {
    return useQuery<ApiResponse<Campaign>>({
        queryKey: [CAMPAIGNS_KEY, id],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<Campaign>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.DETAILS(id)
            );
            return data;
        },
        enabled: !!id,
    });
};

export const useCreateCampaign = () => {
    const queryClient = useQueryClient();

    return useMutation<Campaign, Error, CreateCampaignRequest>({
        mutationFn: async campaignData => {
            const { data } = await httpClient.post<ApiResponse<Campaign>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.CREATE,
                campaignData
            );
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
        },
    });
};

export const useUpdateCampaign = () => {
    const queryClient = useQueryClient();

    return useMutation<Campaign, Error, { id: string; data: UpdateCampaignRequest }>({
        mutationFn: async ({ id, data }) => {
            const { data: responseData } = await httpClient.put<ApiResponse<Campaign>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.UPDATE(id),
                data
            );
            return responseData.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, id] });
        },
    });
};

export const useDeleteCampaign = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: async id => {
            await httpClient.delete(API_ENDPOINTS.CRM.CAMPAIGNS.DELETE(id));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
        },
    });
};

/** Status-only update for the row actions (pause/activate/archive) — thin wrapper over PUT /campaigns/{id}. */
export const useUpdateCampaignStatus = () => {
    const queryClient = useQueryClient();

    return useMutation<Campaign, Error, { id: string; status: CampaignStatus }>({
        mutationFn: async ({ id, status }) => {
            const { data } = await httpClient.put<ApiResponse<Campaign>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.UPDATE(id),
                { status }
            );
            return data.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, id] });
        },
    });
};

// Field catalog, source fields, mapping, preview, token

export const useCampaignFieldCatalog = () => {
    return useQuery<ApiResponse<CampaignFieldCatalog>>({
        queryKey: [CAMPAIGNS_KEY, 'field-catalog'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<CampaignFieldCatalog>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.FIELD_CATALOG
            );
            return data;
        },
    });
};

export const useUpdateCampaignSourceFields = () => {
    const queryClient = useQueryClient();

    return useMutation<Campaign, Error, { id: string; data: UpdateSourceFieldsRequest }>({
        mutationFn: async ({ id, data }) => {
            const { data: responseData } = await httpClient.put<ApiResponse<Campaign>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.SOURCE_FIELDS(id),
                data
            );
            return responseData.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, id] });
        },
    });
};

export const useSuggestCampaignMapping = () => {
    return useMutation<SuggestCampaignMappingResponse, Error, string>({
        mutationFn: async id => {
            const { data } = await httpClient.post<ApiResponse<SuggestCampaignMappingResponse>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.SUGGEST_MAPPING(id)
            );
            return data.data;
        },
    });
};

export const useSaveCampaignMapping = () => {
    const queryClient = useQueryClient();

    return useMutation<
        SaveCampaignMappingResponse,
        Error,
        { id: string; data: SaveCampaignMappingRequest }
    >({
        mutationFn: async ({ id, data }) => {
            const { data: responseData } = await httpClient.put<
                ApiResponse<SaveCampaignMappingResponse>
            >(API_ENDPOINTS.CRM.CAMPAIGNS.MAPPING(id), data);
            return responseData.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, id] });
        },
    });
};

export const usePreviewCampaignMapping = () => {
    return useMutation<
        PreviewCampaignMappingResponse,
        Error,
        { id: string; data: PreviewCampaignMappingRequest }
    >({
        mutationFn: async ({ id, data }) => {
            const { data: responseData } = await httpClient.post<
                ApiResponse<PreviewCampaignMappingResponse>
            >(API_ENDPOINTS.CRM.CAMPAIGNS.PREVIEW(id), data);
            return responseData.data;
        },
    });
};

export const useRotateCampaignToken = () => {
    const queryClient = useQueryClient();

    return useMutation<RotateCampaignTokenResponse, Error, string>({
        mutationFn: async id => {
            const { data } = await httpClient.post<ApiResponse<RotateCampaignTokenResponse>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.ROTATE_TOKEN(id)
            );
            return data.data;
        },
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, id] });
        },
    });
};
